'use client';

import { Suspense } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'next/navigation';
import NavBar from './NavBar';
import { AppErrorBanner } from './common/AppErrorBanner';
import { useAutosave } from '@/hooks/useAutosave';
import { EMBEDDED_QUERY_PARAM, isEmbeddedQueryValue } from '@/lib/embeddedQuery';

/**
 * Main application layout component.
 * Provides the consistent layout structure for the entire application.
 *
 * Features:
 * - Fixed navigation bar at the top (hidden when `?embedded=true`)
 * - Main content area with proper spacing
 * - Responsive design with full viewport height
 *
 * Embedded mode (`embedded=true`): omits NavBar and top offset so routes such as
 * `/publications?embedded=true` and `/data?embedded=true` fill the viewport inside a host iframe.
 *
 * @component
 */
function AppLayoutShell({ children }: { children: React.ReactNode }) {
  useAutosave();
  const searchParams = useSearchParams();
  const isEmbedded = isEmbeddedQueryValue(searchParams.get(EMBEDDED_QUERY_PARAM));

  if (isEmbedded) {
    return (
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          minHeight: '100vh',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <AppErrorBanner />
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 96px)',
        width: '100%',
      }}
    >
      <NavBar />
      <Box
        sx={{
          mt: '64px', // Account for fixed navbar height
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppErrorBanner />
        {children}
      </Box>
    </Box>
  );
}

/**
 * Fallback layout while search params are resolving (matches non-embedded shell).
 */
function AppLayoutFallback({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="main"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 96px)',
        width: '100%',
      }}
    >
      <NavBar />
      <Box
        sx={{
          mt: '64px',
          width: '100%',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <AppErrorBanner />
        {children}
      </Box>
    </Box>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<AppLayoutFallback>{children}</AppLayoutFallback>}>
      <AppLayoutShell>{children}</AppLayoutShell>
    </Suspense>
  );
}
